import http, { LONG_TIMEOUT_MS } from '../http';
import type { Role } from '../contexts/StorageContext';
import { Card, CardType, MarketSnapshot } from '../types/card';
import { JobStatus } from '../types/job';
import { Buyback, BuybackDetail, BuybackItemInput, BuybackPhoto, BuybackReceipt } from '../types/buyback';
import { BuybackListing } from '../types/buybackListing';
import { SuperOverview } from '../types/super';
import { NaverSyncLog } from '../types/naverSyncLog';
import { ApplyCurrentResult, HubChangesResponse } from '../types/hubChanges';
import { ExpansionOption, RegistrableGameType, RegistrableSearchResult } from '../types/productRegistration';
import { StockExcelUploadResult } from '../types/stockExcel';
import { PricehubTestResult, StoreSettingsUpdate, StoreSettingsView } from '../types/settings';
import {
  DecidedBatchItem, DecidedBatchResponse, MAX_DECIDED_BATCH,
  DecidedPriceRequestItem, DecidedPriceResult, DecisionListParams, DecisionListResponse, DecisionMarketItems,
  MAX_DECIDED_BULK, MAX_DECISION_PAGE_SIZE,
} from '../types/decidedPrice';
import { normalizeBatchResponse, normalizeMarketItems, normalizeSaveResult } from '../utils/decidedPrice';
import type { SyncNaverPreview } from '../types/syncPreview';
import { normalizeSyncPreview } from '../utils/syncPreview';
import type { PriceSummary } from '../types/priceSummary';
import { markPriceSummaryStale, normalizePriceSummary } from '../utils/priceSummary';
import type { RerunStarted, RerunStep } from '../types/daily';

// ─── Auth ─────────────────────────────────────────────────────────────────────

// 토큰은 백엔드가 HttpOnly 쿠키로 내려준다(2026-08-19) — 응답 바디에는 화면 권한
// 제어·매장 표시에 쓰는 role/store만 받는다.
//
// timeout을 이 요청에만 짧게(10초) 건다 — http.ts의 기본 axios 인스턴스엔 전역 타임아웃이
// 없어서, 백엔드가 TCP는 받아주는데 응답을 못 내는 상태(2026-09-04 실사고 — 잡 진행률 맵
// 무한 적재로 OOM이 Tomcat 스레드를 죽여 API가 몇 시간째 무응답이었음)면 로그인 버튼이
// 로딩 스피너인 채로 사실상 영원히 멈춘다. 다른 엔드포인트(대량 등록·엑셀 임포트 등)까지
// 전역 타임아웃을 걸면 정상적으로 오래 걸리는 요청이 오작동할 수 있어, 사용자가 결과를
// 빨리 알아야 하는 로그인 한 곳에만 국소적으로 적용한다. LoginForm이 이 요청의 실패
// 유형(타임아웃/네트워크 vs 401)을 구분해 서로 다른 메시지를 보여준다.
export const login = (
  id: string,
  password: string,
): Promise<{ role: Role; store?: string; token?: string; username?: string }> =>
  http.post('/api/auth/login', { id, password }, { timeout: 10_000 }).then((r) => r.data);

export const logout = (): Promise<void> =>
  http.post('/api/auth/logout').then(() => undefined);

// ─── Super (전 매장 총괄) ───────────────────────────────────────────────────────

/** 매장 코드 목록(SUPER 의 매장 전환 UI 용). */
export const fetchStores = (): Promise<string[]> =>
  http.get<string[]>('/api/super/stores').then((r) => r.data);

/** 전 매장 카드를 판매자 상품 코드 기준으로 묶은 횡단 비교. */
export const fetchSuperOverview = (): Promise<SuperOverview> =>
  http.get<SuperOverview>('/api/super/overview').then((r) => r.data);

// ─── Cards ────────────────────────────────────────────────────────────────────

export const fetchCards = (): Promise<Card[]> =>
  http.get<Card[]>('/api/cards').then((r) => r.data);

/** 실제 Naver 반영(가격/재고 PUT) 기록 — "반영 로그" 페이지 전용, 최근 10000건 최신순. */
/** date(yyyy-MM-dd) 없으면 최근 10000건, 있으면 그 하루 전체 — "반영 로그" 일자별 필터. */
export const fetchNaverSyncLogs = (date?: string): Promise<NaverSyncLog[]> =>
  http.get<NaverSyncLog[]>('/api/cards/naver-sync-logs', { params: date ? { date } : undefined }).then((r) => r.data);

/** 가격 롤백(2026-08-04) — 반영 로그에서 체크박스로 선택한 항목(logId 목록)이 가리키는
 * 가격 변경을 전부 그 이전 값으로 되돌려 다시 반영한다. 검색·체크박스로 특정 카드
 * 종류만(예: 디지몬만) 부분 롤백할 수 있다. 잡 큐 패턴(즉시 jobId 반환, 실제 처리는
 * SYNC_NAVER와 동일하게 비동기). */
export const rollbackPrices = (logIds: number[]): Promise<{ jobId: string }> =>
  http.post('/api/cards/rollback-price', logIds).then((r) => r.data);

/**
 * "PriceHub 변경" 목록(2026-10-21) — 수정가(PriceHub 매장 해석값) ≠ 기준가인 카드 전부(rise · drop) +
 * PriceHub 공통가 · 매장가 변경(hub · store) + 매장 직접 변경 확정 대기 알림(notice). since 기본 어제 07:30,
 * kind 는 쉼표로 여러 개. PriceHub 가 응답하지 않으면 로컬 부분만(pricehubAvailable=false).
 */
export const fetchHubChanges = (params?: { since?: string; kind?: string }): Promise<HubChangesResponse> =>
  http.get<HubChangesResponse>('/api/cards/hub-changes', { params }).then((r) => r.data);

/**
 * 네이버 현재가 → PriceHub 매장가(2026-10-21, 백엔드 PR #44) — 선택 카드마다 현재 매장의 PriceHub 매장가를 그 카드의
 * 네이버 현재가로 쓰고 CT 수정가도 같게 맞춘다. 요청 대기열(백엔드 PR #47) 뒤로는 PriceHub 에 요청만 남기고, 매장 직접 변경
 * 알림이 있던 카드면 PriceHub 가 그 알림을 'CT 요청' 으로 덮는다(확정 · 무시는 PriceHub 에서만, 감사 #100). ADMIN/SUPER. 일부 실패는 200 + failed,
 * PriceHub 가 전부 거부하면 502, 매장가 조회가 꺼져 있거나 ids 가 200장을 넘으면 400(화면이 200장씩 나눠 보낸다),
 * 가격 조회 · 재고 동기화 잡이 도는 중이면 409.
 */
export const applyCurrentToHub = (
  ids: number[],
  opts?: { signal?: AbortSignal; store?: string | null },
): Promise<ApplyCurrentResult> =>
  http.post<ApplyCurrentResult>(
    '/api/cards/hub-store-price/apply-current',
    // store = 이 카드들을 받은 hub-changes 목록의 매장(감사 card-controltower#66, 백엔드 PR #45) — 요청 매장과 다르면 서버가
    // 그 카드를 failed("다른 매장")로 빼고 PriceHub 를 부르지 않는다. 목록에 매장이 없으면(예전 응답) 싣지 않는다.
    opts?.store ? { ids, store: opts.store } : { ids },
    { signal: opts?.signal },
  ).then((r) => r.data)
    // 서버가 칩 건수 요약 캐시를 비운다(백엔드 PR #93) — 화면도 다시 받게 알린다(실패 · 결과 불명도 서버가 바뀌었을 수 있다).
    .finally(markPriceSummaryStale);

/**
 * 가격 화면 칩 건수 요약(감사 card-controltower#745, 백엔드 PR #93) — 확정 대기 · 대상 아님 등. 로그인한 누구나(VIEWER 포함), 읽기만.
 * 모양이 다른 응답은 null, 404(옛 서버) · 오류는 그대로 던진다 — 부른 쪽이 건수를 비운다.
 */
export const fetchPriceSummary = (): Promise<PriceSummary | null> =>
  http.get<unknown>('/api/cards/price-summary').then((r) => normalizePriceSummary(r.data));

export const updatePriceManually = (id: number, price: number): Promise<Card> =>
  http.patch<Card>(`/api/cards/${id}/price`, { price }).then((r) => r.data);

/** 하락 확인 리뷰에서 보여줄 PriceHub 판매처 목록 — 포커스된 카드 1건만 그때그때 조회.
 * PriceHub에 없거나 조회 실패 시 404 → null(리뷰 자체는 계속 진행 가능해야 하므로). */
// 판매가 결정(감사 card-controltower#341)의 계약이 이 경로를 새 모양(shop · url · imageUrl · summary)으로 바꾼다 — 백엔드(#340)가 바꾼
// 뒤에도 하락 검토가 깨지지 않게 두 모양 다 옛 MarketSnapshot 으로 읽는다.
export const fetchMarketItems = (id: number): Promise<MarketSnapshot | null> =>
  http.get<unknown>(`/api/cards/${id}/market-items`)
    .then((r) => {
      const n = normalizeMarketItems(r.data);
      return {
        items: n.items.map((i) => ({ mallName: i.shop, title: i.title, price: i.price, link: i.url, image: i.imageUrl })),
        minPrice: n.summary?.min ?? null,
        maxPrice: n.summary?.max ?? null,
        avgPrice: n.summary?.avg ?? null,
        collectedAt: n.collectedAt,
      };
    })
    .catch((e) => {
      if (e?.response?.status === 404) return null;
      throw e;
    });

// ─── 판매가 결정(감사 card-controltower#341 · 백엔드 #340 과 같은 계약) ─────────────────────────────────────────

/**
 * 판매가 결정 목록 — 확장팩 · 검색어 · 레어도 · 미설정만으로 서버에서 거르고 page(0부터) · size(≤200)로 나눠 받는다.
 * 빈 값은 싣지 않는다.
 */
export const fetchDecisionList = (
  params: DecisionListParams,
  opts?: { signal?: AbortSignal },
): Promise<DecisionListResponse> => {
  const query: Record<string, string | number | boolean> = {
    page: Math.max(0, params.page),
    size: Math.min(Math.max(1, params.size), MAX_DECISION_PAGE_SIZE),
  };
  if (params.expansion) query.expansion = params.expansion;
  if (params.q && params.q.trim()) query.q = params.q.trim();
  if (params.game) query.game = params.game;
  // 레어도 여럿은 쉼표 하나로(백엔드가 쉼표 · 반복 파라미터 둘 다 읽는다 — axios 기본 배열 직렬화 rarity[]= 는 못 읽는다).
  const rarities = (params.rarities ?? []).map((r) => r.trim()).filter(Boolean);
  if (rarities.length > 0) query.rarity = rarities.join(',');
  if (params.unsetOnly) query.unsetOnly = true;
  return http.get<DecisionListResponse>('/api/cards/decision-list', { params: query, signal: opts?.signal })
    .then((r) => ({
      items: Array.isArray(r.data?.items) ? r.data.items : [],
      total: typeof r.data?.total === 'number' ? r.data.total : 0,
    }));
};

/** 판매가 결정 오른쪽 패널의 판매처 목록 — PriceHub 에 없으면(404) null. */
export const fetchDecisionMarketItems = (
  id: number,
  opts?: { signal?: AbortSignal },
): Promise<DecisionMarketItems | null> =>
  http.get<unknown>(`/api/cards/${id}/market-items`, { signal: opts?.signal })
    .then((r) => normalizeMarketItems(r.data))
    .catch((e) => {
      if (e?.response?.status === 404) return null;
      throw e;
    });

/**
 * 판매가 한 건 저장 → 네이버 반영 · PriceHub 보고. requestId 는 멱등 키 — 결과 불명(naver.status=UNKNOWN · 응답 없음)이면 같은
 * 가격 · 같은 requestId 로만 다시 보낸다.
 */
export const saveDecidedPrice = (
  id: number,
  body: { price: number; requestId: string },
  /** store — 확인 창을 연 시점 매장으로 고정(감사 #845, http.ts storeOverride). 없으면 보내는 순간의 매장. */
  opts?: { store?: string | null },
): Promise<DecidedPriceResult> =>
  (opts && 'store' in opts
    ? http.put<unknown>(`/api/cards/${id}/decided-price`, body, { storeOverride: opts.store ?? null })
    : http.put<unknown>(`/api/cards/${id}/decided-price`, body))
    .then((r) => normalizeSaveResult(r.data, { cardId: id, price: body.price }));

/**
 * 판매가 여러 건 저장(≤200, 넘으면 부른 쪽이 나눈다) — 백엔드(#340, PR #69)는 잡 큐로 돌리고 {jobId} 만 바로 준다. 항목별 결과는
 * {@link getJobStatus} 의 decisions(요청 순서, 단건 응답과 같은 모양) — utils/decidedPrice 의 normalizeBulkResults 로 cardId 별로 읽는다.
 */
export const startDecidedPricesBulk = (
  items: DecidedPriceRequestItem[],
): Promise<{ jobId: string }> => {
  if (items.length > MAX_DECIDED_BULK) {
    return Promise.reject(new Error(`한 번에 ${MAX_DECIDED_BULK}건까지 보낼 수 있습니다`));
  }
  return http.post<{ jobId: string }>('/api/cards/decided-price/bulk', { items }).then((r) => r.data);
};

/**
 * 판매가 여러 장 결정(백엔드 PR #97, 감사 card-controltower#649 · #819) — 매장 가격 쓰기 락을 한 번 잡고 요청 순서대로 처리한다(단건을
 * 여러 번 동시에 보내면 락이 겹쳐 409 가 났다). 20장까지(넘으면 부른 쪽이 나눈다). 일부만 저장돼도 200. 서버가 락을 쥔 채 카드마다
 * 네이버를 직렬로 부르니 LONG_TIMEOUT_MS(120초) — 기본 30초면 '응답 없음' 뒤에도 서버는 계속 쓴다. 409 는 본문 `{message, retryAfterMs}`,
 * 옛 서버는 404(부른 쪽이 단건으로).
 */
export const saveDecidedPricesBatch = (items: DecidedBatchItem[]): Promise<DecidedBatchResponse> => {
  if (items.length > MAX_DECIDED_BATCH) {
    return Promise.reject(new Error(`한 번에 ${MAX_DECIDED_BATCH}건까지 보낼 수 있습니다`));
  }
  return http.post<unknown>('/api/cards/decided-price/batch', { items }, { timeout: LONG_TIMEOUT_MS })
    .then((r) => normalizeBatchResponse(r.data, items));
};
export const deleteCard = (id: number): Promise<void> =>
  http.delete(`/api/cards/${id}`).then(() => undefined);

export const deleteCards = (ids: number[]): Promise<{ deleted: number }> =>
  http.delete('/api/cards/bulk', { data: ids }).then((r) => r.data);

// ─── Jobs ─────────────────────────────────────────────────────────────────────

export const fetchStatusForCards = (ids: number[]): Promise<{ jobId: string }> =>
  http.post('/api/cards/fetch-status', ids).then((r) => r.data);

export const updateAllPrices = (ids: number[]): Promise<{ jobId: string }> =>
  http.post('/api/cards/update-prices', ids).then((r) => r.data);

/** opts.store — 확인 창을 연 시점 매장으로 고정(감사 #828, http.ts storeOverride). 없으면 보내는 순간의 매장. */
export const syncPricesToNaver = (ids: number[], opts?: { store?: string | null }): Promise<{ jobId: string }> =>
  http.post('/api/cards/sync-naver', ids, opts && 'store' in opts ? { storeOverride: opts.store ?? null } : undefined).then((r) => r.data);

/**
 * ③ 스토어 반영 확인 창 요약(감사 card-controltower#538, 백엔드 PR #76) — 본문은 /sync-naver 와 같은 ids. 읽기 전용.
 * 모양이 다른 응답은 null(확인 창은 요약 없이), 404(옛 서버) · 오류는 그대로 던진다 — 부른 쪽이 요약 없이 묻는다.
 */
export const previewSyncNaver = (ids: number[], opts?: { store?: string | null }): Promise<SyncNaverPreview | null> =>
  http.post<unknown>('/api/cards/sync-naver/preview', ids, opts && 'store' in opts ? { storeOverride: opts.store ?? null } : undefined)
    .then((r) => normalizeSyncPreview(r.data));

/**
 * 타 샵 시세 대조 자동 분류(잡) — ① 수정 가격 조회 완료 후 자동으로 이어서 호출한다.
 * 하락 카드를 IN_RANGE / SUSPECT / NO_REFERENCE로 분류해 각 카드에 영속하고, 갱신된
 * 카드 목록을 잡 결과로 돌려준다. dropIds를 비우면 매장 전체 하락 카드가 대상.
 */
export const classifyDrops = (dropIds: number[]): Promise<{ jobId: string }> =>
  http.post('/api/cards/classify-drops', dropIds).then((r) => r.data);

/** 엑셀 업로드를 대체하는 신규 등록: 네이버 스토어 전체 상품을 조회해 DB에 없는 것만 추가 */
export const importFromNaver = (): Promise<{ jobId: string }> =>
  http.post('/api/cards/import-from-naver').then((r) => r.data);

export const getJobStatus = (jobId: string): Promise<JobStatus> =>
  http.get(`/api/cards/jobs/${jobId}`).then((r) => r.data);

// ─── Product Registration (신상품 등록 — 포켓몬/원피스/디지몬, 2026-08-18) ─────

/** 신상품 등록 대상으로 고를 수 있는 확장팩 목록(PriceHub 카탈로그 기준). */
export const fetchRegistrableExpansions = (gameType: RegistrableGameType): Promise<ExpansionOption[]> =>
  http.get<ExpansionOption[]>('/api/cards/registrable-expansions', { params: { gameType } }).then((r) => r.data);

/**
 * "확장팩 일괄 등록" 실행 전 미리보기(2026-08-31, "상품 등록 개선안") — 고른 확장팩에서
 * 새로 생성될 상품과 이미 등록돼 건너뛸 상품을 상품명까지 먼저 보여준다. registrable-search
 * 와 같은 RegistrableSearchResult 형태.
 */
export const fetchExpansionRegisterPreview = (
  gameType: RegistrableGameType, expansionCode: string,
): Promise<RegistrableSearchResult[]> =>
  http.get<RegistrableSearchResult[]>(
    `/api/cards/registrable-expansions/${encodeURIComponent(expansionCode)}/preview`,
    { params: { gameType } },
  ).then((r) => r.data);

/** 카드명/카드번호로 검색해서 개별 등록할 후보를 찾는다("검색해서 등록" 기능). */
export const searchRegistrableCards = (
  gameType: RegistrableGameType, q: string,
): Promise<RegistrableSearchResult[]> =>
  http.get<RegistrableSearchResult[]>('/api/cards/registrable-search', { params: { gameType, q } })
    .then((r) => r.data);

/**
 * 신상품을 네이버에 등록(판매중지 상태로 시작) — expansionCode면 그 확장팩 전체,
 * productCodes면 검색해서 고른 특정 코드만(둘 중 하나만 넣는다).
 * stockByCode: productCodes 등록 시 코드별 초기 재고(선택, 2026-08-24) — 없는 코드는 1로 채워진다.
 */
// 백엔드가 jobId 만 즉시 돌려주고 실제 등록은 잡으로 도는 비동기 API 다(CardController.java) —
// 응답을 오래 기다릴 이유가 없어 기본 타임아웃(30초)을 쓴다. 엑셀 업로드(동기 파싱)만 120초.
export const registerProducts = (
  params: {
    gameType: RegistrableGameType; expansionCode?: string; productCodes?: string[];
    stockByCode?: Record<string, number>;
  },
): Promise<{ jobId: string }> =>
  http.post('/api/cards/register-products', params).then((r) => r.data);

/**
 * "검색 개별 등록"에서 검색해도 안 나온 카드(카탈로그 미등록)를 사진으로 제보한다
 * (2026-08-25). 카드 정보 입력 없이 사진만 pricehub 제보함으로 올라간다 — note는
 * 선택 사항.
 */
export const submitRegistrationRequest = (
  gameType: RegistrableGameType, image: File, note?: string,
): Promise<{ success: boolean }> => {
  const form = new FormData();
  form.append('image', image);
  form.append('gameType', gameType);
  if (note) form.append('note', note);
  return http.post('/api/cards/registration-requests', form).then((r) => r.data);
};

/**
 * 아침 카드의 실패 카드만 다시 돌리기(백엔드 PR #95, 감사 card-controltower#796) — 동기화 · 가격 받기 · 상승분 자동 반영.
 * 200 `{ chainId, step, jobId, requested, message }` — jobId 로 기존 잡 폴링(`GET /api/cards/jobs/{jobId}`), null 이면 대상 없음.
 * 400 단계 · ids 잘못 · SUPER 매장 미지정, 403 VIEWER, 409 아침 체인 · 다른 재실행 · 같은 종류 잡 진행 중 · 자동 반영 매장 스위치 꺼짐.
 * 옛 서버는 404 — 부른 쪽이 '이동만'(반영 로그 보기)으로 둔다.
 */
export const rerunMorningStep = (step: RerunStep, ids: number[]): Promise<RerunStarted> =>
  http.post<{ jobId?: unknown; message?: unknown; requested?: unknown }>('/api/cards/morning-chain/rerun', { step, ids })
    .then((r) => ({
      jobId:     typeof r.data?.jobId === 'string' && r.data.jobId !== '' ? r.data.jobId : null,
      message:   typeof r.data?.message === 'string' && r.data.message !== '' ? r.data.message : null,
      requested: typeof r.data?.requested === 'number' ? r.data.requested : null,
    }));

// ─── Stock (재고 관리 — 가격과 분리된 신규 기능) ───────────────────────────────

/** 재고 동기화: Naver 현재 재고를 DB 로 취득 (선택 ids 또는 빈 배열=전체) */
export const syncStock = (ids: number[]): Promise<{ jobId: string }> =>
  http.post('/api/cards/sync-stock', ids).then((r) => r.data);

/**
 * 재고 반영: 카트의 "현재 재고에 대한 증감량"(delta)을 Naver 로 넘긴다. 절대 재고수량으로
 * 바꿔 보내지 않는다 — 자동 재고 동기화가 하루 1회(07:30)뿐이라, 화면에서 본 재고로 계산한
 * 절대값을 그대로 PUT하면 그 사이 들어온 실시간 주문을 덮어써 오버셀이 난다. 백엔드가 반영
 * 직전 Naver 실측 재고에 이 증감량을 더한다(2026-09-04).
 */
/**
 * 재고 반영. requestId(감사 card-controltower#277, 백엔드 PR #58 StockDelta.requestId)는 카트 항목마다 화면이 만드는 멱등 키 —
 * 같은 증감을 다시 보낼 때(재시도)는 같은 값을 보낸다. 직전 반영이 결과 불명이면 백엔드가 이 값으로 '같은 요청의 재시도' 를
 * 알아보고 이중 가산 없이 확정한다. 없으면(옛 화면) 결과 불명이 풀릴 때까지 그 카드 반영을 막는다.
 */
export const applyStock = (
  items: { id: number; delta: number; requestId?: string }[],
): Promise<{ jobId: string }> =>
  http.post('/api/cards/apply-stock', items).then((r) => r.data);

/**
 * 신규 확장팩 재고 조사용 엑셀 다운로드(2026-08-26) — 확장팩마다 매장에 엑셀로 실물
 * 재고를 받아 수기로 스토어에 입력하던 절차를 대체한다. Blob으로 받아 브라우저에서
 * 바로 저장 트리거만 걸면 된다(StockToolbar 참고).
 */
export const downloadStockExcelTemplate = (
  gameType: CardType, expansionCode: string,
): Promise<Blob> =>
  http.get('/api/cards/stock/excel-template', {
    params: { gameType, expansionCode },
    responseType: 'blob',
  }).then((r) => r.data);

/** 매장이 수량을 채워 회신한 엑셀 업로드 — DB에는 쓰지 않고 상품코드 매칭 결과만 돌려준다. */
export const uploadStockExcel = (
  gameType: CardType, file: File,
): Promise<StockExcelUploadResult> => {
  const form = new FormData();
  form.append('file', file);
  form.append('gameType', gameType);
  return http.post('/api/cards/stock/excel-upload', form, { timeout: LONG_TIMEOUT_MS }).then((r) => r.data);
};

// ─── Buyback (매입 관리 — 상태별 오더 관리) ────────────────────────────────────

/** 매입 오더 목록(최신순, 전체 상태). 상태 필터는 화면에서 처리한다. */
export const fetchBuybacks = (): Promise<Buyback[]> =>
  http.get<Buyback[]>('/api/buybacks').then((r) => r.data);

/**
 * 신규(NEW_ORDER) 매입 오더만 조회(스태프 알람용). 상태 필터를 서버에서 걸어
 * 완료/불성립 오더가 쌓여도 폴링 응답이 가벼운 신규 건만 담기게 한다.
 */
export const fetchNewBuybacks = (): Promise<Buyback[]> =>
  http.get<Buyback[]>('/api/buybacks', { params: { status: 'NEW_ORDER' } }).then((r) => r.data);

/** 매입 오더 상세(명세 포함, 개인정보 포함). */
export const fetchBuybackDetail = (id: number): Promise<BuybackDetail> =>
  http.get<BuybackDetail>(`/api/buybacks/${id}`).then((r) => r.data);

/** 신규 → 준비중("매입을 진행하시겠습니까?" 확인 후). */
export const prepareBuyback = (id: number): Promise<BuybackDetail> =>
  http.post<BuybackDetail>(`/api/buybacks/${id}/prepare`).then((r) => r.data);

/** 카트 저장(명세 전량 교체). */
export const saveBuybackCart = (id: number, items: BuybackItemInput[]): Promise<BuybackDetail> =>
  http.put<BuybackDetail>(`/api/buybacks/${id}/items`, items).then((r) => r.data);

/** 준비중 → 준비 완료. */
export const markBuybackReady = (id: number): Promise<BuybackDetail> =>
  http.post<BuybackDetail>(`/api/buybacks/${id}/ready`).then((r) => r.data);

// ─── Buyback 실물 사진(첨부는 선택 · ADMIN/SUPER) ──────────────────────────────

/** 오더에 첨부된 사진 메타 목록. */
export const fetchBuybackPhotos = (id: number): Promise<BuybackPhoto[]> =>
  http.get<BuybackPhoto[]>(`/api/buybacks/${id}/photos`).then((r) => r.data);

/** 실물 카드 사진 첨부(멀티파트). */
export const uploadBuybackPhoto = (id: number, file: Blob, filename: string): Promise<BuybackPhoto> => {
  const form = new FormData();
  form.append('file', file, filename);
  return http.post<BuybackPhoto>(`/api/buybacks/${id}/photos`, form).then((r) => r.data);
};

/**
 * 사진 원본을 blob 으로 받아 온다(인증이 필요한 스트리밍이라 <img src> 직접 지정 대신
 * 이 blob 으로 objectURL 을 만들어 표시한다).
 */
export const fetchBuybackPhotoBlob = (id: number, photoId: number): Promise<Blob> =>
  http.get(`/api/buybacks/${id}/photos/${photoId}`, { responseType: 'blob' }).then((r) => r.data);

/** 사진 삭제. */
export const deleteBuybackPhoto = (id: number, photoId: number): Promise<void> =>
  http.delete(`/api/buybacks/${id}/photos/${photoId}`).then(() => undefined);

/** 완료된 매입 오더의 영수증 데이터(매장 정보·내역·총액, 개인정보 없음). */
export const fetchBuybackReceipt = (id: number): Promise<BuybackReceipt> =>
  http.get<BuybackReceipt>(`/api/buybacks/${id}/receipt`).then((r) => r.data);

// ─── Buyback Listing (매입 리스트 — 직원이 선정한 매입 희망 상품 목록) ──────────
//
// 위 Buyback(손님 접수 오더)과는 다른 개념: 매장이 능동적으로 "삽니다"로 선정해 게시하는
// 큐레이션 목록이다. 상품 검색은 기존 searchRegistrableCards 를 그대로 재사용한다.

/**
 * 현재 매장의 매입 리스트(게임·날짜별, 백엔드가 발매일 내림차순 → 카드번호 오름차순으로
 * 정렬해 내려 준다). date 를 생략하면 오늘(백엔드가 직전 날짜를 복제해 지연 생성).
 */
export const fetchBuybackListings = (
  gameType: string,
  date?: string,
): Promise<BuybackListing[]> =>
  http
    .get<BuybackListing[]>('/api/buyback-listings', { params: { gameType, date } })
    .then((r) => r.data);

/** 해당 게임의 리스트가 존재하는 날짜 목록(최신순, 오늘 포함) — 날짜 선택기용. */
export const fetchBuybackListingDates = (gameType: string): Promise<string[]> =>
  http
    .get<string[]>('/api/buyback-listings/dates', { params: { gameType } })
    .then((r) => r.data);

/** 검색 결과에서 고른 상품을 오늘 리스트에 담는다(같은 날 중복 상품코드는 409). */
export const addBuybackListing = (item: {
  gameType: string;
  shopProductCode: string;
  cardNumber: string | null;
  expansionCode: string | null;
  productName: string;
  imageUrl: string | null;
  price?: number | null;
}): Promise<BuybackListing> =>
  http.post<BuybackListing>('/api/buyback-listings', item).then((r) => r.data);

/** 단가 수정(직원이 수시로 조정). null 이면 미정으로 되돌린다. */
export const updateBuybackListingPrice = (id: number, price: number | null): Promise<BuybackListing> =>
  http.patch<BuybackListing>(`/api/buyback-listings/${id}`, { price }).then((r) => r.data);

/** 리스트에서 제거. */
export const deleteBuybackListing = (id: number): Promise<void> =>
  http.delete(`/api/buyback-listings/${id}`).then(() => undefined);

// ─── Settings (매장별 외부 연동 설정 — PriceHub 연결 · 상품 등록 기본값, 2026-09-01) ─────

/** 대상 매장은 백엔드가 결정한다(ADMIN=토큰 매장, SUPER=X-Store-Code 헤더). */
export const fetchStoreSettings = (): Promise<StoreSettingsView> =>
  http.get<StoreSettingsView>('/api/settings/store').then((r) => r.data);

/** pricehubApiKey를 빈 문자열로 보내면 기존 키를 유지한다. */
export const saveStoreSettings = (payload: StoreSettingsUpdate): Promise<StoreSettingsView> =>
  http.put<StoreSettingsView>('/api/settings/store', payload).then((r) => r.data);

/**
 * 화면에서 저장한 PriceHub API Key 를 지운다 — 그 뒤로는 서버 설정값(yml · .env)을 쓴다(감사 card-controltower#99).
 * 서버가 갱신된 설정을 돌려주면 그것을, 본문이 없으면(204) null 을 준다 — null 이면 부른 쪽이 다시 읽는다.
 */
export const clearPricehubApiKey = (): Promise<StoreSettingsView | null> =>
  http.delete<StoreSettingsView | ''>('/api/settings/store/pricehub-api-key')
    .then((r) => (r.data && typeof r.data === 'object' && 'storeCode' in r.data ? r.data : null));

/** 저장된 PriceHub 설정으로 실제 요청을 한 번 보내 연결 확인. 먼저 저장한 뒤 부른다. */
export const testPricehubConnection = (): Promise<PricehubTestResult> =>
  http.post<PricehubTestResult>('/api/settings/store/pricehub-test').then((r) => r.data);
